import { formatCurrencyGbp, formatNumber, ukDate } from "./calculations";
import {
  DEFAULT_BENEFITS_TEXT,
  DEFAULT_PILOT_TEXT,
  DEFAULT_RISKS_TEXT,
  DEFAULT_DECISION_REQUEST,
} from "./defaults";

const safeText = (value, fallback) => (String(value || "").trim() ? String(value).trim() : fallback);

export function buildBusinessCaseText(values, calc) {
  const company = values.companyName || "the employer";
  const campaign = values.campaignName || "the target recruitment campaign";
  const preparedBy = values.preparedBy || "the recruitment lead";

  const netPositive = calc.summary.perCampaignNetValue > 0;
  const recommendation = netPositive
    ? "Subject to validation of the assumptions, appropriate data-protection arrangements and retention of all company and industry selection requirements, the model supports a controlled pilot of the Railway Careers UK Talent Pool."
    : "The assumptions entered do not currently demonstrate a net financial benefit. The proposed recruitment funnel, time requirements and route-specific costs should be reviewed before approval.";

  const roiMultiple = calc.summary.valuePerPound > 0 ? `${formatNumber(calc.summary.valuePerPound, 1)}×` : "—";

  const lines = [
    "1. Executive summary",
    `${company} has modelled ${campaign} using the Railway Careers UK Talent Pool Business Case Builder. The headline finding is straightforward: the Talent Pool releases ${formatNumber(calc.summary.internalCapacityReleasedHours, 1)} internal staff-hours per campaign — an indicative value of ${formatCurrencyGbp(calc.summary.internalCapacityValue)} — against a Talent Pool cost of just ${formatCurrencyGbp(calc.summary.talentPoolCostPerCampaign)}. That is a ${roiMultiple} return on every £1 of fee paid. Across ${formatNumber(values.campaignsPerYear)} comparable campaigns per year, the indicative net value is ${formatCurrencyGbp(calc.summary.annualisedValue)}.`,
    "",
    "2. Value-for-money signal",
    `Talent Pool cost per campaign: ${formatCurrencyGbp(calc.summary.talentPoolCostPerCampaign)} (£${formatNumber(calc.summary.feePerSuccessfulHire, 0)} per successful hire × ${formatNumber(calc.summary.successfulRailwayHires, 0)} expected hires). Indicative value of hours released: ${formatCurrencyGbp(calc.summary.internalCapacityValue)}. Net campaign value: ${formatCurrencyGbp(calc.summary.perCampaignNetValue)}. For every £1 of Talent Pool fee the model returns approximately ${roiMultiple} in indicative internal capacity value.`,
    "",
    "3. Current position",
    `${company} currently expects ${formatNumber(values.conventionalApplicationsReceived)} applications, ${formatNumber(values.conventionalGatewayPass)} gateway passes, ${formatNumber(values.conventionalInterviewed)} interviews and ${formatNumber(values.appointmentsRequired)} appointments for this campaign, with significant recruiter and operational panel demand.`,
    "",
    "4. Proposed Railway Careers UK Talent Pool approach",
    `The proposed approach narrows front-end review volume to ${formatNumber(values.talentProfilesReviewed)} talent-pool profiles and ${formatNumber(values.talentInterviewed)} interviews while retaining all mandatory employer and industry controls before final appointment.`,
    "",
    "5. Capacity comparison",
    `Conventional route total staff-hours: ${formatNumber(calc.conventional.totalStaffHours, 1)}. Talent Pool route total staff-hours: ${formatNumber(calc.talent.totalStaffHours, 1)}. Released capacity: ${formatNumber(calc.summary.internalCapacityReleasedHours, 1)} hours (${formatNumber(calc.summary.workingPersonDaysReleased, 1)} working days). Interview panel-days avoided: ${formatNumber(calc.summary.interviewPanelDaysAvoided, 1)}.`,
    "",
    "6. Commercial position",
    `Indicative internal value of released hours: ${formatCurrencyGbp(calc.summary.internalCapacityValue)}. Talent Pool cost: ${formatCurrencyGbp(calc.summary.talentPoolCostPerCampaign)} per campaign. Net value per campaign: ${formatCurrencyGbp(calc.summary.perCampaignNetValue)}. Annualised net value: ${formatCurrencyGbp(calc.summary.annualisedValue)}.`,
    "",
    "7. Wider operational benefits",
    safeText(values.additionalExpectedBenefits, DEFAULT_BENEFITS_TEXT),
    "",
    "8. Risks and controls",
    safeText(values.risksAndControls, DEFAULT_RISKS_TEXT),
    "",
    "9. Proposed pilot",
    safeText(values.recommendedPilotScope, DEFAULT_PILOT_TEXT),
    "",
    "10. Measurement framework",
    "Track campaign-level funnel movement, recruiter hours, manager panel-hours, panel-day utilisation, Talent Pool fees paid and net value against this baseline model.",
    "",
    "11. Recommendation",
    recommendation,
    "",
    "12. Important interpretation of savings",
    "Salary-derived value normally represents internal capacity released rather than an automatically cashable budget saving. Treat value as cashable only where equivalent budget spend is genuinely avoided.",
    "",
    "Decision context",
    `Prepared by: ${preparedBy}`,
    `Target decision date: ${ukDate(values.targetDecisionDate)}`,
    `Decision requested: ${safeText(values.decisionRequested, DEFAULT_DECISION_REQUEST)}`,
    "",
    "Assurance statement",
    "Railway Careers UK supports candidate identification and pre-screening. Employers retain responsibility for all legal, regulatory, safety-critical, medical, psychometric, equality, data-protection and appointment decisions.",
  ];

  return lines.join("\n");
}

export default function BusinessCasePreview({ values, calc }) {
  const text = buildBusinessCaseText(values, calc);
  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h3 className="font-display font-bold text-xl text-ink">Draft business case</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Live draft generated from your assumptions. Replace any narrative fields to tailor for your approval workflow.
      </p>
      <pre className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-ink font-sans">{text}</pre>
    </section>
  );
}
